import { useCallback, useEffect, useState } from 'react';
import { ClerkProvider, SignInButton, SignUpButton, UserButton, useAuth } from '@clerk/react';
import App from './App.jsx';
import './AuthenticatedApp.css';

function AccessScreen({ children }) {
  return <main className="auth-screen">
    <section className="auth-card" aria-labelledby="auth-title">
      <p className="auth-eyebrow">Wound care intelligence</p>
      <h1 id="auth-title">AskWoundBot</h1>
      {children}
      <p className="auth-footnote">Evidence-linked wound care support.</p>
    </section>
  </main>;
}

function SessionApp() {
  const { isLoaded, isSignedIn, userId, sessionId, getToken } = useAuth();
  const [approvedSession, setApprovedSession] = useState(null);
  const [consultationsEnabled, setConsultationsEnabled] = useState(false);
  const [denied, setDenied] = useState(false);
  const authenticatedFetch = useCallback(async (path, options = {}) => {
    if (!['/api/query', '/api/prompts', '/api/session'].includes(path)) throw new Error('Invalid endpoint');
    const token = await getToken();
    if (!token) throw new Error('Sign-in required');
    const response = await fetch(path, { ...options, credentials: 'same-origin', cache: 'no-store',
      headers: { ...options.headers, Authorization: `Bearer ${token}` } });
    if (response.status === 401 || response.status === 403) {
      setApprovedSession(null);
      setDenied(true);
    }
    return response;
  }, [getToken]);
  useEffect(() => {
    let active = true;
    setApprovedSession(null);
    setConsultationsEnabled(false);
    setDenied(false);
    if (isSignedIn && sessionId) {
      authenticatedFetch('/api/session').then(async response => {
        const data = response.ok ? await response.json() : null;
        if (active) {
          setApprovedSession(data?.approved === true ? sessionId : null);
          setConsultationsEnabled(data?.approved === true && data?.consultationsEnabled === true);
          setDenied(data?.approved !== true);
        }
      }).catch(() => { if (active) setDenied(true); });
    }
    return () => { active = false; };
  }, [isSignedIn, sessionId, authenticatedFetch]);
  if (!isLoaded) return <AccessScreen><p role="status">Loading secure access…</p></AccessScreen>;
  if (!isSignedIn) return <AccessScreen>
    <p className="auth-intro">Sign in to your AskWoundBot account.</p>
    <p>Create an account to request access to your workspace.</p>
    <nav className="auth-actions" aria-label="Account access">
      <SignInButton mode="modal"><button className="auth-button auth-button-primary" type="button">Sign in</button></SignInButton>
      <SignUpButton mode="modal"><button className="auth-button" type="button">Create account</button></SignUpButton>
    </nav>
  </AccessScreen>;
  return <div className="authenticated-shell">
    <header className="auth-header">
      <div className="auth-brand"><strong>AskWoundBot</strong><span>Wound care intelligence</span></div>
      <p className="auth-notice">Evidence-linked wound care support</p>
      <div className="auth-account"><span>Account</span><UserButton /></div>
    </header>
    <div className="auth-workspace">
      {sessionId && approvedSession === sessionId
        ? consultationsEnabled
          ? <App key={`${userId}:${sessionId}`} request={authenticatedFetch} apiBase="/api" />
          : <AccessScreen><p className="auth-intro">Your account is ready.</p><p role="status">Consultations are currently unavailable. Please try again later.</p></AccessScreen>
        : <AccessScreen><p role="status">{denied ? 'We couldn’t open your workspace. Check your account access and try again.' : 'Checking access…'}</p></AccessScreen>}
    </div>
  </div>;
}

export default function AuthenticatedApp() {
  const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!key) return <AccessScreen><p>Secure access is awaiting configuration.</p></AccessScreen>;
  return <ClerkProvider publishableKey={key} afterSignOutUrl="/" telemetry={false}><SessionApp /></ClerkProvider>;
}
